import configs from "../configs";
import { getAuth0Token } from "../ui/utils";

export default class FolderService {
  async getFolders(request, { root, workspaceId }) {
    let url = workspaceId ? `workspaces/${workspaceId}/folder-scenes` : `folder-scenes`;
    if (root) url += `?root=${root}`;
    try {
      const token = await getAuth0Token();
      const response = await request(`${configs.CUSTOM_API_URL}/${url}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      const json = await response.json();

      return json.data;
    } catch (e) {
      console.error(e);
    }

    return null;
  }

  async createFolder(request, { body, workspaceId }) {
    try {
      const token = await getAuth0Token();
      const url = workspaceId ? `workspaces/${workspaceId}/folder-scenes` : `folder-scenes`;
      const response = await request(`${configs.CUSTOM_API_URL}/${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (json.error) {
        throw new Error(json.error);
      }

      return json.data;
    } catch (e) {
      console.error(e);
      return e.message;
    }
  }

  async updateFolder(request, { folder, body, workspaceId }) {
    try {
      const token = await getAuth0Token();
      const url = workspaceId ? `workspaces/${workspaceId}/folder-scenes/${folder.id}` : `folder-scenes/${folder.id}`;
      const response = await request(`${configs.CUSTOM_API_URL}/${url}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      if (json.error) {
        throw new Error(json.error);
      }

      return json.data;
    } catch (e) {
      console.error(e);
      return e.message;
    }
  }

  async deleteFolder(request, { folder, workspaceId }) {
    try {
      const token = await getAuth0Token();
      const url = workspaceId ? `workspaces/${workspaceId}/folder-scenes/${folder.id}` : `folder-scenes/${folder.id}`;
      const response = await request(`${configs.CUSTOM_API_URL}/${url}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      const json = await response.json();

      return json.data;
    } catch (e) {
      console.error(e);
    }

    return null;
  }

  async recoverFolder(request, { folder, workspaceId }) {
    try {
      const token = await getAuth0Token();
      const url = workspaceId
        ? `workspaces/${workspaceId}/folder-scenes/${folder.id}/restore`
        : `folder-scenes/${folder.id}/restore`;
      const response = await request(`${configs.CUSTOM_API_URL}/${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      const json = await response.json();

      return json.data;
    } catch (e) {
      console.error(e);
    }

    return null;
  }

  async addToFolder(request, { folder, project, workspaceId }) {
    try {
      const token = await getAuth0Token();
      const body = {
        folder_scene_id: folder,
        project_id: project.project_id,
        scene_id: project.scene_id,
        ar_code: project.ar ? project.ar.code : null
      };

      const url = workspaceId ? `workspaces/${workspaceId}/folder-scenes/projects` : `folder-scenes/projects`;

      const response = await request(`${configs.CUSTOM_API_URL}/${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      return json.data;
    } catch (e) {
      console.error(e);
    }

    return null;
  }

  async addToFolderMultiple(request, { folder, projects, type = "project", workspaceId }) {
    try {
      if (!folder) {
        const error = new Error("Folder is required to add projects to a collection");
        console.error("addToFolderMultiple error:", error.message, { folder, projects, type });
        throw error;
      }

      const token = await getAuth0Token();
      const body = {
        folder_scene_id: folder,
        projects_id: projects.map(item => item.project_id || item.id), // use item.id for scenes
        type
      };

      const url = workspaceId ? `workspaces/${workspaceId}/folder-scenes/projects` : `folder-scenes/projects`;

      const response = await request(`${configs.CUSTOM_API_URL}/${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      return json;
    } catch (e) {
      console.error("addToFolderMultiple error:", e);
      throw e;
    }
  }

  async removeOfFolder(request, { projects, folder, workspaceId }) {
    try {
      const token = await getAuth0Token();
      const body = {
        folder_id: folder?.id,
        project_ids: projects.map(project => project.project_id || project.id) // use item.id for scenes
      };

      const url = workspaceId
        ? `workspaces/${workspaceId}/folder-scenes/projects/unsync`
        : `folder-scenes/projects/unsync`;

      const response = await request(`${configs.CUSTOM_API_URL}/${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });

      const json = await response.json();

      return json.data;
    } catch (e) {
      console.error(e);
    }
  }

  async getFolderProjects(request, { folder, workspaceId }) {
    try {
      const token = await getAuth0Token();
      const url = workspaceId ? `workspaces/${workspaceId}/folder-scenes/${folder}` : `folder-scenes/${folder}`;
      const response = await request(`${configs.CUSTOM_API_URL}/${url}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      const json = await response.json();

      return json.data && json.data.length ? json.data : [];
    } catch (e) {
      console.error(e);
    }

    return null;
  }

  async addProjectToMultipleFolders(request, { folders, projects, type = "project", workspaceId }) {
    try {
      const token = await getAuth0Token();
      const body = {
        folder_scenes_id: folders.map(folder => folder.id),
        project_ids: projects.map(item => item.project_id || item.id), // use item.id for scenes
        type // add type: 'project' or 'scene'
      };

      const url = workspaceId
        ? `workspaces/${workspaceId}/folder-scenes/projects/folders`
        : `folder-scenes/projects/folders`;

      const response = request(`${configs.CUSTOM_API_URL}/${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      });

      return response;
    } catch (e) {
      console.error(e);
    }

    return null;
  }
}
